import { tpoSignupConfigurationApi } from 'app/services/api';

import { SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE = createNamedActions('tpoSignupConfigurations', {
  GET_LIST: 'GET_LIST',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  SET_DEFAULT: 'SET_DEFAULT',
});

export const TPO_SIGNUP_STEP_CONDITION_EXPRESSION_SCHEMA_ACTION_TYPE = createNamedActions(
  'tpoSignupStepConditionExpressionSchema',
  {
    GET: 'GET',
  },
);

// The form-bundle modal fetches this speculatively, to classify an expression the author never
// asked about — pass `skipResponseInterceptor` there so a 403 for someone without TPO signup
// access degrades quietly instead of tripping the global forbidden-error page reload.
export const getTpoSignupStepConditionExpressionDefinitionAction = catchAsyncActionError(
  ({ skipResponseInterceptor } = {}) =>
    async dispatch => {
      const { data } = await tpoSignupConfigurationApi.getStepConditionExpressionDefinition({
        skipResponseInterceptor,
      });

      return dispatch({
        type: TPO_SIGNUP_STEP_CONDITION_EXPRESSION_SCHEMA_ACTION_TYPE.GET,
        payload: data,
      });
    },
);

export const getTpoSignupConfigurationsAction = catchAsyncActionError(
  ({ isForbiddenSilent = false } = {}) =>
    async dispatch => {
      const { data } = await tpoSignupConfigurationApi.getTpoSignupConfigurations(
        getConditionalObject(isForbiddenSilent, SKIP_FORBIDDEN_RESPONSE_CONFIG),
      );

      return dispatch({
        type: TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE.GET_LIST,
        payload: data,
      });
    },
);

export const createTpoSignupConfigurationAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await tpoSignupConfigurationApi.createTpoSignupConfiguration(params);

    return dispatch({
      type: TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE.CREATE,
      payload: data,
    });
  },
);

export const updateTpoSignupConfigurationAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await tpoSignupConfigurationApi.updateTpoSignupConfiguration(params);

    return dispatch({
      type: TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE.UPDATE,
      payload: data,
    });
  },
);

export const deleteTpoSignupConfigurationAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      await tpoSignupConfigurationApi.deleteTpoSignupConfiguration({ id });

      return dispatch({
        type: TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE.DELETE,
        payload: { id },
      });
    },
);

export const setTpoSignupConfigurationDefaultAction = catchAsyncActionError(
  ({ id }) =>
    async dispatch => {
      await tpoSignupConfigurationApi.setTpoSignupConfigurationDefault({ id });

      return dispatch({
        type: TPO_SIGNUP_CONFIGURATIONS_ACTION_TYPE.SET_DEFAULT,
        payload: { id },
      });
    },
);

export const getTpoSignupConfigurationAction = catchAsyncActionError(({ id }) => async () => {
  const { data } = await tpoSignupConfigurationApi.getTpoSignupConfiguration({ id });

  return data;
});

export const uploadTpoSignupConfigurationImageAction = catchAsyncActionError(
  ({ id, type, formData }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.uploadTpoSignupConfigurationImage({
        id,
        type,
        formData,
      });

      return data;
    },
);

export const getTpoSignupConfigurationDomainsAction = catchAsyncActionError(
  ({ id }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.getTpoSignupConfigurationDomains({ id });

      return data;
    },
);

export const assignTpoSignupConfigurationDomainAction = catchAsyncActionError(
  ({ id, domainName }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.assignTpoSignupConfigurationDomain({
        id,
        domainName,
      });

      return data;
    },
);

export const removeTpoSignupConfigurationDomainAction = catchAsyncActionError(
  ({ id, domainName }) =>
    async () => {
      await tpoSignupConfigurationApi.removeTpoSignupConfigurationDomain({ id, domainName });

      return { domainName };
    },
);

export const setTpoSignupConfigurationDomainDefaultAction = catchAsyncActionError(
  ({ id, domainName }) =>
    async () => {
      await tpoSignupConfigurationApi.setDomainDefault({ id, domainName });

      return { domainName };
    },
);

export const getTpoFormBundleCriteriaAction = catchAsyncActionError(
  ({ id, isForbiddenSilent = false }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.getFormBundleCriteria({
        id,
        config: getConditionalObject(isForbiddenSilent, SKIP_FORBIDDEN_RESPONSE_CONFIG),
      });

      return data;
    },
);

export const createTpoFormBundleCriteriaAction = catchAsyncActionError(
  ({ id, body }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.createFormBundleCriteria({ id, body });

      return data;
    },
);

export const updateTpoFormBundleCriteriaAction = catchAsyncActionError(
  ({ id, criteriaId, body }) =>
    async () => {
      const { data } = await tpoSignupConfigurationApi.updateFormBundleCriteria({
        id,
        criteriaId,
        body,
      });

      return data;
    },
);

export const deleteTpoFormBundleCriteriaAction = catchAsyncActionError(
  ({ id, criteriaId }) =>
    async () => {
      await tpoSignupConfigurationApi.deleteFormBundleCriteria({ id, criteriaId });

      return { criteriaId };
    },
);
